import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import Cache from '../../service/Cache';

import Accessory from '../../components/Accessory';
import Room from '../../components/Room';
import RoomSelector from '../../components/RoomSelector';
import SideOptions from '../../components/SideOptions';
import Tutorial from '../../components/Tutorial';
import LoadingSpinner from '../../components/LoadingSpinner';
import Popup from '../Popup';

import styles from './styles.module.scss';

let resizeTimeout;

const roomContainerSpec = {
  maxWidth: 1600,
};
const sideBarWidth = {
  desktop: 460,
  tablet: 340,
  mobile: 0,
};
const paddingWidth = {
  desktop: 44 * 2,
  tablet: 44 * 2,
  mobile: 30 * 2,
};

const Configurator = ({ state, setState, roomTypes, accessories }) => {
  const { t } = useTranslation();

  const cache = useMemo(() => new Cache(), []);

  // Refs
  const containerRef = useRef();
  const activeElementRef = useRef(null);
  const tooltipRef = useRef(null);

  // Room state
  const [room, setRoom] = useState(null);

  // Accessory states
  const [toggledAccessory, setToggledAccessory] = useState(null);

  // Notification state
  const [showPopup, setShowPopup] = useState(false);
  const [itemsAddedCount, setItemsAddedCount] = useState(0);

  // Tutorial states
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // Tutorial start
  const getIsTutorialMode = (step) => {
    return showTutorial && tutorialStep === step;
  };

  const getWindowWidth = () => {
    return window.document.children[0].clientWidth;
  };

  // Get window width
  const getResizeWidth = (windowWidth) => {
    if (windowWidth > 1365) {
      return 840;
    } else if (windowWidth > 1023) {
      return windowWidth - sideBarWidth.desktop - paddingWidth.desktop - 50;
    } else if (windowWidth > 768) {
      return windowWidth - sideBarWidth.tablet - paddingWidth.tablet;
    } else if (windowWidth < 400) {
      return 400 - sideBarWidth.mobile - paddingWidth.mobile;
    } else {
      return windowWidth - sideBarWidth.mobile - paddingWidth.mobile;
    }
  };

  // Resize room + layers
  const resizeImages = useCallback((room) => {
    let windowWidth = window.document.children[0].clientWidth;
    let currentRoomWidth = state.original_room_width;

    const widthRatio = currentRoomWidth / getResizeWidth(windowWidth);
    const roomWidth = currentRoomWidth / widthRatio;

    setRoom({
      ...room,
      style_width: roomWidth,
    });
  }, [state]);

  const handleResize = useCallback(() => {
    if (resizeTimeout) clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
      roomTypes.forEach((roomType) => {
        if (roomType.key !== state.roomKey) return;
        resizeImages(roomType);
      });
    }, 500);
  }, [roomTypes, state.roomKey, resizeImages]);

  // Select room
  const roomTypeSelection = (roomKey) => {
    setState({ ...state, roomKey: roomKey });
  };

  const closeDialog = () => {
    setToggledAccessory(null);
  };

  const onResult = () => {
    setState({
      ...state,
      key: 'final',
      roomKey: null,
    });

    window.parent.postMessage(
      {
        name: 'scrollToTop',
      },
      '*'
    );

    // Adobe Analytics
    if (window.globalLmd) {
      window.lmd = window.globalLmd;
      window.lmd.custom = t('tool_ui.button.view_final');
      window._satellite.track('CustomInteraction');
    }
  };

  const showItemAdded = (count) => {
    if (count === 0) {
      setItemsAddedCount(0);
      setShowPopup(false);
      return;
    }

    setItemsAddedCount((prevCount) => prevCount + count);
    setShowPopup(true);

    if (resizeTimeout) clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
      setShowPopup(false);
      setItemsAddedCount(0);
    }, 2500);
  };

  useEffect(() => {
    // Sync cache with state
    cache.set('k-12-selectedAccessories', state.selectedAccessories);
  }, [state.selectedAccessories, cache]);

  useEffect(() => {
    const hasCompletedTutorial = cache.get('hasCompletedK12Tutorial');

    if (!hasCompletedTutorial) {
      setTutorialStep(1);
      setShowTutorial(true);
      cache.set('hasCompletedK12Tutorial', 'true');
    }
  }, [setState, cache]);

  useEffect(() => {
    // tooltip positioning
    const adjustToolTipPosition = () => {
      if (activeElementRef.current && tooltipRef.current) {
        const rect = activeElementRef.current.getBoundingClientRect();
        const toolTipRect = tooltipRef.current.getBoundingClientRect();
        const windowWidth = getWindowWidth();

        // desktop
        if (windowWidth > 1024) {
          if (tutorialStep <= 1) {
            const marginTop = 20;
            tooltipRef.current.style.top = `${rect.bottom + marginTop}px`;
            tooltipRef.current.style.left = `${rect.left}px`;
          } else if (tutorialStep === 2) {
            tooltipRef.current.style.top = `120px`;
            tooltipRef.current.style.left = `${rect.left - 200}px`;
          } else {
            const marginRight = 50;
            tooltipRef.current.style.top = `${rect.top}px`;
            tooltipRef.current.style.left = `${
              rect.left + rect.width + marginRight
            }px`;
          }
        } else {
          // mobile and tablet
          let marginTop = 50;
          let marginBottom = 10;
          const paddingLeft = 32;
          if (tutorialStep <= 1) {
            tooltipRef.current.style.top = `${rect.bottom + marginTop + 20}px`;
            tooltipRef.current.style.left = `${rect.left + paddingLeft}px`;
          } else {
            tooltipRef.current.style.top = `${
              rect.top - rect.height - toolTipRect.height - marginBottom
            }px`;
            tooltipRef.current.style.left = `${rect.left}px`;
          }
        }
      }
    };

    adjustToolTipPosition();

    window.addEventListener('resize', adjustToolTipPosition);

    return () => {
      window.removeEventListener('resize', adjustToolTipPosition);
    };
  }, [tutorialStep]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    roomTypes.forEach((roomType) => {
      if (roomType.key === state.roomKey) {
        resizeImages(roomType);
        return;
      }
    });
  }, [state.roomKey, roomTypes, resizeImages]);

  if (!room) return <LoadingSpinner />;

  return (
    <div className={styles.configurator}>
      <div className={styles.roomContainer}>
        {showTutorial && (
          <Tutorial
            tooltipRef={tooltipRef}
            tutorialStep={tutorialStep}
            setTutorialStep={setTutorialStep}
            setShowTutorial={setShowTutorial}
            t={t}
          />
        )}
        <RoomSelector
          roomTypes={roomTypes}
          roomTypeSelection={roomTypeSelection}
          state={state}
          closeDialog={closeDialog}
          activeElementRef={activeElementRef}
          isTutorialMode={getIsTutorialMode(1)}
          t={t}
        />
        {showPopup && (
          <Popup
            itemCount={itemsAddedCount}
            desktop={true}
            onResult={onResult}
            state={state}
          />
        )}
        <div
          className={styles.container}
          ref={containerRef}
          style={roomContainerSpec}
        >
          {room && (
            <div className={`row ${styles.mobileDescription}`}>
              <p>{t(room.description)}</p>
            </div>
          )}
          <SideOptions
            room={room}
            roomAccessories={accessories[room.key]}
            state={state}
            setState={setState}
            toggledAccessory={toggledAccessory}
            setToggledAccessory={setToggledAccessory}
            onResult={onResult}
            activeElementRef={activeElementRef}
            isTutorialMode={getIsTutorialMode(3)}
            showItemAdded={showItemAdded}
            t={t}
          />
          <div
            className={styles.room}
            style={{ zIndex: getIsTutorialMode(2) ? 20 : 'auto' }}
          >
            {accessories[room.key].map((accessory, idx) => (
              <Accessory
                state={state}
                key={accessory.key}
                accessory={accessory}
                room={room}
                idx={idx}
                toggledAccessory={toggledAccessory}
                setToggledAccessory={setToggledAccessory}
                isTutorialMode={getIsTutorialMode(2)}
              />
            ))}
            {showPopup && (
              <Popup
                itemCount={itemsAddedCount}
                desktop={false}
                onResult={onResult}
                state={state}
              />
            )}
            <Room
              room={room}
              toggledAccessory={toggledAccessory}
              activeElementRef={activeElementRef}
              isTutorialMode={getIsTutorialMode(2)}
            />
            {!showTutorial && (
              <img
                src={`${process.env.PUBLIC_URL}/images_iax/icons/logo-intel.svg`}
                alt='intel logo'
                className={styles.intelLogo}
              ></img>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configurator;
